/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import {
  Modal,
  ModalBody,
  ModalButton,
  ModalFooter,
  ModalHeader,
} from "baseui/modal";
import { ThemeType } from "../DesignSystem/Theme/Theme";
import {
  getTertiaryButtonOverrides,
  getOutlinedButtonOverrides,
} from "../DesignSystem/ButtonColorStyles";
import { FaRedo } from "react-icons/fa";

interface ErrorModalProps {
  theme: ThemeType;
  isOpen: boolean;
  fileName: string;
  errorMessage: string;
  onClose: () => void;
  onRetry?: () => void;
}

export const QuickFormErrorModal: React.FC<ErrorModalProps> = ({
  theme,
  isOpen,
  fileName,
  errorMessage,
  onClose,
  onRetry,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      overrides={{
        Dialog: {
          style: {
            backgroundColor: theme.colors.tableBackgroundColor,
            maxWidth: "500px",
          },
        },
      }}
    >
      <ModalHeader
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing.md,
          backgroundColor: theme.colors.tableBackgroundColor,
          color: theme.colors.defaultTextColor,
          fontFamily: theme.fonts.primary,
          fontSize: theme.fontSizes.large,
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 16 16"
          fill="none"
          stroke={theme.colors.negative}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <path d="M8 2L14.5 13.5H1.5L8 2Z" />
          <line x1="8" y1="7" x2="8" y2="10" />
          <circle
            cx="8"
            cy="12"
            r="0.6"
            fill={theme.colors.negative}
            stroke="none"
          />
        </svg>
        Transformation Error Details
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: theme.colors.background,
          color: theme.colors.text,
          fontFamily: theme.fonts.primary,
        }}
      >
        <div style={{ marginBottom: theme.spacing.md }}>
          <div
            style={{
              background: "rgba(239, 68, 68, 0.08)",
              padding: theme.spacing.md,
              borderRadius: "6px",
              marginBottom: theme.spacing.md,
              borderLeft: `3px solid ${theme.colors.negative}`,
            }}
          >
            <strong
              style={{
                display: "block",
                color: theme.colors.negative,
                marginBottom: "4px",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              File
            </strong>
            <p
              style={{
                color: theme.colors.text,
                fontSize: theme.fontSizes.medium,
                lineHeight: "1.5",
                margin: 0,
              }}
            >
              {fileName}
            </p>
          </div>

          <div
            style={{
              background: "rgba(239, 68, 68, 0.08)",
              padding: theme.spacing.md,
              borderRadius: "6px",
              marginBottom: theme.spacing.md,
              borderLeft: `3px solid ${theme.colors.negative}`,
            }}
          >
            <strong
              style={{
                display: "block",
                color: theme.colors.negative,
                marginBottom: "4px",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Error Message
            </strong>
            <p
              style={{
                color: theme.colors.text,
                fontSize: theme.fontSizes.medium,
                lineHeight: "1.5",
                margin: 0,
              }}
            >
              {errorMessage}
            </p>
          </div>

          <div
            style={{
              background: "rgba(239, 68, 68, 0.08)",
              padding: theme.spacing.md,
              borderRadius: "6px",
              borderLeft: `3px solid ${theme.colors.negative}`,
            }}
          >
            <strong
              style={{
                display: "block",
                color: theme.colors.negative,
                marginBottom: "4px",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Suggested Action
            </strong>
            <p
              style={{
                color: theme.colors.text,
                fontSize: theme.fontSizes.medium,
                lineHeight: "1.5",
                margin: 0,
              }}
            >
              Please verify the file format and structure, then try transforming
              again.
            </p>
          </div>
        </div>
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: theme.colors.tableBackgroundColor,
          display: "flex",
          gap: theme.spacing.sm,
          justifyContent: "flex-end",
        }}
      >
        <ModalButton
          onClick={onClose}
          overrides={getTertiaryButtonOverrides(theme)}
        >
          Close
        </ModalButton>
        {onRetry && (
          <ModalButton
            onClick={onRetry}
            overrides={getOutlinedButtonOverrides(theme)}
          >
            <FaRedo size={14} style={{ marginRight: "6px" }} />
            Retry Transformation
          </ModalButton>
        )}
      </ModalFooter>
    </Modal>
  );
};
